import { flow } from '@1x2network/flow'
import { BasicLayout } from '@1x2network/layout'
import { BezierCurve, ParticleTrail, ParticleTrailSpecification } from '@1x2network/particle_trail'
import { Component, ContainerSpecification, createSpine, Spine, SpineSpecification } from '@1x2network/pixi_assets'
import { AssetPool, PositionTuple, TradReelSet } from '@1x2network/reel_set'
import {
    createNudgeEffect,
    EffectManager,
    Shake2D,
    ShakeStartOptions,
    ShakeStopOptions,
    TweenVars,
} from '@1x2network/tweens'
import { arrayUtils } from '@1x2network/utils_array'
import gsap from 'gsap'
import { Container, ICanvas, IRenderer, Point } from 'pixi.js'
import { Signal } from 'typed-signals'
import { CustomSymbol } from '../CustomSymbol'
import { SymbolModel } from '../model/SymbolModel'
import { ShapeOptions } from '../types/ShapeOptions'
import { makeShape } from '../utils/shapes/makeShape'
import { toSeconds } from '../utils/toSeconds'
import { MiddleReel } from './MiddleReel'
import { toMs } from '../utils/toMs'

type CoinShowerArgs = {
    destination: Point
    transform: {
        position: PositionTuple
        data: SymbolModel
    }
}

/**
 * Animation to transform symbols on the board by having them shoot out of the logo
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class CoinShower extends Component<CoinShowerSpecification> {
    private reelSet: TradReelSet<CustomSymbol, SymbolModel>
    private shake: Shake2D
    private layout: BasicLayout
    private effectManager: EffectManager
    private cashSymbols: CustomSymbol[] = []
    private collectorSymbols: CustomSymbol[] = []
    private renderer: IRenderer<ICanvas>
    private spine: Spine
    private middleReel: MiddleReel
    private explosionPool: AssetPool<Spine>

    public readonly onAnticipate = new Signal()
    public readonly onEmit = new Signal()
    public readonly onLand = new Signal()
    public readonly onTrainVisible = new Signal()
    public readonly onTrainHidden = new Signal()

    constructor(options: {
        parent?: Container
        layout: BasicLayout
        shake: Shake2D
        reelSet: TradReelSet<CustomSymbol, SymbolModel>
        effectManager: EffectManager
        renderer: IRenderer<ICanvas>
        middleReel: MiddleReel
    }) {
        super({ spec: 'coinShower', parent: options.parent })
        this.layout = options.layout
        this.shake = options.shake
        this.reelSet = options.reelSet
        this.effectManager = options.effectManager
        this.renderer = options.renderer
        this.spine = createSpine({ spec: this.specification.reelAnimation, parent: this })
        this.middleReel = options.middleReel
        this.explosionPool = new AssetPool(() => createSpine({ spec: this.specification.effect }))
    }

    public async animateBottomToTop(duration: number): Promise<void> {
        this.spine.visible = true
        this.spine.alpha = 0

        const timeline = gsap
            .timeline()
            .fromTo(
                this.spine,
                {
                    pixi: {
                        positionX: 418,
                        positionY: 700,
                    },
                },
                {
                    pixi: {
                        positionX: 418,
                        positionY: -1334,
                    },
                    duration,
                    ease: 'none',
                    onStart: () => this.onTrainVisible.emit(),
                },
            )
            .to(this.spine, { alpha: 1, ease: 'slow(0.75,1,true)', duration }, '<')
            .call(() => this.onTrainHidden.emit(), [], '-=0.3')

        await timeline

        this.spine.visible = false
    }

    public async animateEmpty(): Promise<void> {
        this.middleReel.in()
        await flow.wait(300)
        this.onEmit.emit()
        await flow.wait(500)
        await this.animateBottomToTop(2.5)
    }

    public async animate(events: Array<CoinShowerArgs>): Promise<void> {
        const shakeStartSpec = this.specification.tweens.shakeStart
        const shakeStopSpec = this.specification.tweens.shakeStop

        await flow.wait(400)
        this.onAnticipate.emit()
        this.shake.start(shakeStartSpec)
        await flow.wait(1000)
        this.middleReel.in()
        await flow.wait(300)
        this.onEmit.emit()
        await flow.wait(500)

        const masterTimeline = gsap.timeline()
        const duration = 200

        this.animateBottomToTop(3.7)

        // Animate each row sequentially
        let c = 0
        for (let i = 2; i >= 0; i--) {
            const transformsInThisRow = events.filter((transformArgs) => transformArgs.transform.position[1] === i)
            if (transformsInThisRow.length) {
                masterTimeline.add(this.doSingleRow(transformsInThisRow), toSeconds(1066 + duration * c))
            }
            c++
        }

        await masterTimeline
        await flow.wait(150)

        this.shake.stop(shakeStopSpec)
        await flow.wait(150)
    }

    private doSingleRow(transforms: Array<CoinShowerArgs>): GSAPTimeline {
        const duration = 1600
        const replaceDuration = 750
        const timeline = gsap.timeline()

        const [middleTransforms, shootOutTransforms] = arrayUtils.split(transforms, (transform) => {
            return transform.transform.position[0] === 2
        })

        // There can only be one middle transform per row
        const middleTransform = middleTransforms[0]

        // Order by how close the position is to the center column
        shootOutTransforms.sort((a, b) => {
            const aDistance = Math.abs(a.transform.position[0] - 2)
            const bDistance = Math.abs(b.transform.position[0] - 2)
            return bDistance - aDistance
        })

        shootOutTransforms.forEach((transform, i) => {
            const percentage = (i + 1) / (shootOutTransforms.length + 1)
            const delay = toSeconds(duration * percentage)
            timeline.add(this.animateShootOut(transform, percentage), delay)
        })

        if (middleTransform) {
            // Transform when the train overlaps it
            timeline.add(() => {
                // Transform animation
                const symbol = this.reelSet.pool.requestLandSymbol(middleTransform.transform.data)
                symbol.zIndex = 3
                symbol.blurred = false
                symbol.position.copyFrom(middleTransform.destination)
                this.reelSet.topContainer.addChild(symbol)
                this.cashSymbols.push(symbol)
            }, toSeconds(replaceDuration))
        }

        return timeline
    }

    private animateShootOut(options: CoinShowerArgs, percentage: number): GSAPAnimation {
        const rowYPositions = [-192, 0, 192, 384]
        const { transform, destination } = options
        const row = transform.position[1]
        const startX = 416
        const startY = gsap.utils.interpolate(rowYPositions[row + 1], rowYPositions[row], percentage)

        const shootOutAnimation = this.specification.shootOutAnimation
        const symbol = this.reelSet.pool.requestLandSymbol(transform.data)
        symbol.zIndex = 3
        symbol.blurred = false
        symbol.scale.set(0)
        symbol.visible = false
        this.cashSymbols.push(symbol)

        const { trailSpec } = this.specification
        const { steps, tweenVars, shape: shapeOptions } = shootOutAnimation
        const shape = makeShape(shapeOptions.kind, shapeOptions.options)
        const points = shape.calculateControlPoints(new Point(startX, startY), destination)
        const bezier = new BezierCurve(points, steps)
        const trail = new ParticleTrail(bezier, trailSpec)

        const trailTween = trail.animate({
            ...tweenVars,
            onParticlesComplete: () => {
                trail.destroy()
            },
            onUpdate: (point) => {
                symbol.position.copyFrom(point)
            },
            onStart: () => {
                symbol.playCollect()
                this.addChild(trail, symbol)
            },
        })

        const explosionSpine = this.explosionPool.getAsset()
        explosionSpine.position.copyFrom(destination)

        return gsap
            .timeline()
            .add(trailTween)
            .to(symbol.scale, {
                x: 1,
                y: 1,
                delay: 0.05,
                duration: 0.2,
                ease: 'back.out(1)',
                onStart: () => {
                    symbol.visible = true
                },
            })
            .call(
                () => {
                    const trackEntry = explosionSpine.play('tumbling')
                    trackEntry.time = 0.1
                    this.reelSet.topContainer.addChild(symbol, explosionSpine)

                    // Clean up explosion when done
                    flow.wait(toMs(trackEntry.animation.duration)).then(() =>
                        this.explosionPool.returnAsset(explosionSpine),
                    )
                },
                [],
                '<-=0.3',
            )
            .add(
                this.effectManager.addAnimation(
                    this.reelSet,
                    createNudgeEffect({ targetOffset: { y: 5 }, duration: 0.25 }),
                ),
                '<',
            )
            .call(
                () => {
                    this.onLand.emit()
                },
                [],
                '<',
            )
    }

    public reset(): void {
        this.collectorSymbols.forEach((symbol) => {
            this.reelSet.pool.returnSymbol(symbol)
        })
        this.cashSymbols.forEach((symbol) => {
            this.reelSet.pool.returnSymbol(symbol)
        })
        this.cashSymbols = []
        this.collectorSymbols = []
    }
}
export interface CoinShowerSpecification extends ContainerSpecification {
    effect: SpineSpecification
    reelAnimation: SpineSpecification
    trailSpec: ParticleTrailSpecification
    shootOutAnimation: {
        shape: ShapeOptions
        steps: number
        tweenVars: TweenVars
    }
    tweens: {
        shakeStart: ShakeStartOptions
        shakeStop: ShakeStopOptions
    }
}
